import type { CapabilityConfig } from '../../types';
import {
  hasOnlyKeysFromSet,
  isValidContextConfig,
  isValidContext,
} from '../../utils';
import type * as MultiplayerCall from './call';
import type * as MultiplayerRoom from './room';

export type Constraints =
  | MultiplayerCall.Constraints
  | MultiplayerRoom.Constraints;

export enum CommandType {
  Message = 'message',
  Action = 'action',
}

export type ParticipantId = string;

export type ParticipantProps = {
  username?: string;
  avatar?: string;
  inviteId?: string;
  ip?: string;
  sessionId?: string;
};

// what we need from the host app
export type InternalParticipant = ParticipantProps & {
  id: ParticipantId;
};

// Payload that is sent through realtime.sendMessage()
export type Command = {
  instanceId: string;
  type: string;
  payload: unknown;
};

export type Message = {
  type: string;
  // Keep it simple for now but we can make it more complex if necessary
  payload: Record<string, unknown>;
};

export type MessageCommand = Command & {
  type: CommandType.Message;
  payload: Message;
};

export type ParticipantMessage = Message & {
  participant: ParticipantId;
};

export type Action = {
  action: string;
  payload: unknown;
};

export type ActionCommand = Command & {
  type: CommandType.Action;
  payload: Action;
};

export type ParticipantAction = Action & {
  participant: ParticipantId;
};

export type MultiplayerScope = 'room' | 'call';

export type MultiplayerMessageHandler = (message: ParticipantMessage) => void;

export const VALID_CONTEXT_KEYS = [
  'id',
  'username',
  'avatar',
  'inviteId',
  'ip',
  'sessionId',
] as const;
export const REQUIRED_CONTEXT_KEYS = ['id'] as const;

// what is exposed to extensions
export type Participant = ParticipantProps & {
  id?: ParticipantId;
};

const VALID_CONFIG_KEYS = ['type', 'context'] as const;
export type MultiplayerConfig = CapabilityConfig & {
  context: (keyof Participant)[];
};

export function isValidConfig(
  config: CapabilityConfig,
): config is MultiplayerConfig {
  if (!hasOnlyKeysFromSet(config, VALID_CONFIG_KEYS)) {
    return false;
  }

  if (!isValidContextConfig(config, VALID_CONTEXT_KEYS)) {
    return false;
  }

  return true;
}

export function isValidParticipant(
  participant: unknown,
): participant is InternalParticipant {
  return isValidContext<InternalParticipant>(
    participant,
    VALID_CONTEXT_KEYS,
    REQUIRED_CONTEXT_KEYS,
  );
}
